import { createSelector, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { selectElements } from "./assetSlice";
import type { BridgeElement } from "~/types/bridge";
import type { CloudImage } from "~/types/CloudImage";
import type { ClassificationState } from "~/types/cesium";
import { setInspectionPoint } from "./cesiumSlice";
import type { REFERENCE_LINE, TRANSLATION_LINE } from "~/components/cesiumComponents/constants";
import type { RootState } from "~/store";
import { fetchSurvey } from "~/thunks/surveyThunks";

interface EntitiesComntrollerState {
  selectedObjectIds: string[];
  selectedImage: CloudImage | null;
  imagesLocationsUpdated: number;
  referenceLine: string | null;
  translationLine: string | null;
  allTilesLoaded: boolean;
  /** Per-model classification visibility/color overrides, keyed by modelId. */
  classifications: Record<string, Record<number, ClassificationState>>;

};

const initialState: EntitiesComntrollerState = {
  selectedObjectIds: [],
  selectedImage: null,
  imagesLocationsUpdated: 0,
  referenceLine: null,
  translationLine: null,
  allTilesLoaded: false,
  classifications: {},

};

const componentSlice = createSlice({
  name: "entitiesController",
  initialState,
  reducers: {
    setSelectedObjectIds: (state, action: PayloadAction<string[]>) => {
      state.selectedObjectIds = action.payload;
    },
    setSelectedImage: (state, action: PayloadAction<CloudImage>) => {
      state.selectedImage = action.payload;
    },
    toggleObjectId: (state, action: PayloadAction<string>) => {
      const objectId = action.payload;
      if (state.selectedObjectIds.includes(objectId)) {
        state.selectedObjectIds = state.selectedObjectIds.filter(id => id !== objectId);
      } else {
        state.selectedObjectIds.push(objectId);
      }

    },
    incrementImagesLocationsUpdated: (state) => {
      state.imagesLocationsUpdated += 1;
    },
    setReferenceLine: (state, action: PayloadAction<string | null>) => {
      state.referenceLine = action.payload;
    },
    setTranslationLine: (state, action: PayloadAction<string | null>) => {
      state.translationLine = action.payload;
    },
    setAllTilesLoaded: (state, action: PayloadAction<boolean>) => {
      state.allTilesLoaded = action.payload;
    },
    setModelClassifications: (state, action: PayloadAction<{ modelId: string; classifications: Record<number, ClassificationState> }>) => {
      const { modelId, classifications } = action.payload;
      state.classifications[modelId] = classifications;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchSurvey.pending, (state, action) => {
        Object.assign(state, initialState);
      })
  },
});

export const { toggleObjectId, 
  setSelectedObjectIds, setSelectedImage, incrementImagesLocationsUpdated,
  setReferenceLine, setTranslationLine, setAllTilesLoaded, setModelClassifications
 } = componentSlice.actions;
export const selectEntitiesController = (state: RootState) => state.entitiesController;
export default componentSlice.reducer;
export const selectedObjectIds = (state: RootState) => state.entitiesController.selectedObjectIds;
export const selectImagesLocationsUpdated = (state: RootState) => state.entitiesController.imagesLocationsUpdated;
export const selectSelectedImage = (state: RootState) => state.entitiesController.selectedImage;
export const selectReferenceLine = (state: RootState) => state.entitiesController.referenceLine;
export const selectTranslationLine = (state: RootState) => state.entitiesController.translationLine;
export const selectAllTilesLoaded = (state: RootState) => state.entitiesController.allTilesLoaded;
export const selectModelClassifications = (state: RootState, modelId?: number | string | null) =>
  modelId !== undefined && modelId !== null ? state.entitiesController.classifications[String(modelId)] : undefined;
export const makeSelectSelectedElements = createSelector(
  [selectElements, selectedObjectIds],
  (elements: BridgeElement[], selectedObjectIds) => elements.filter((element) => selectedObjectIds.includes(element.object_id))
);